import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../services/api';

const TrackingContext = createContext();

export const useTracking = () => useContext(TrackingContext);

export const TrackingProvider = ({ children }) => {
  const [trackingConfig, setTrackingConfig] = useState({
    gtmId: '',
    ga4Id: '',
    fbPixelId: '',
    googleAdsId: '',
    googleAdsLabel: '',
    googleAdsConversionId: '',
    tiktokPixelId: ''
  });
  const [loaded, setLoaded] = useState(false);

  // 平台級 GA4 (Landing / Apply 等無租戶頁面用)
  const PLATFORM_GA4_ID = process.env.REACT_APP_PLATFORM_GA4_ID || 'G-R5C58BQZM4';

  // 載入追蹤碼設定
  useEffect(() => {
    const loadTrackingConfig = async () => {
      try {
        const response = await api.get('/settings/tracking/public');
        if (response.data.success) {
          const config = response.data.config || {};
          setTrackingConfig({
            gtmId: config.gtm_id || '',
            ga4Id: config.ga4_id || PLATFORM_GA4_ID,
            fbPixelId: config.fb_pixel_id || '',
            googleAdsId: config.google_ads_id || '',
            googleAdsLabel: config.google_ads_label || '',
            googleAdsConversionId: config.google_ads_conversion_id || '',
            tiktokPixelId: config.tiktok_pixel_id || ''
          });
        }
      } catch (error) {
        // API 失敗（無租戶）→ fallback 到平台 GA4
        setTrackingConfig(prev => ({ ...prev, ga4Id: PLATFORM_GA4_ID }));
      } finally {
        setLoaded(true);
      }
    };

    loadTrackingConfig();
  }, [PLATFORM_GA4_ID]);

  // 載入 GTM
  useEffect(() => {
    if (loaded && trackingConfig.gtmId) {
      const script = document.createElement('script');
      script.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${trackingConfig.gtmId}');
      `;
      document.head.appendChild(script);
    }
  }, [loaded, trackingConfig.gtmId]);

  // 載入 GA4 (如果沒有使用 GTM)
  useEffect(() => {
    if (loaded && trackingConfig.ga4Id && !trackingConfig.gtmId) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingConfig.ga4Id}`;
      document.head.appendChild(script);

      const inlineScript = document.createElement('script');
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${trackingConfig.ga4Id}');
      `;
      document.head.appendChild(inlineScript);
    }
  }, [loaded, trackingConfig.ga4Id, trackingConfig.gtmId]);

  // 載入 Facebook Pixel
  useEffect(() => {
    if (loaded && trackingConfig.fbPixelId) {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${trackingConfig.fbPixelId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);
    }
  }, [loaded, trackingConfig.fbPixelId]);

  // 載入 Google Ads (如果沒有使用 GTM)
  useEffect(() => {
    if (loaded && trackingConfig.googleAdsId && !trackingConfig.gtmId) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingConfig.googleAdsId}`;
      document.head.appendChild(script);

      const inlineScript = document.createElement('script');
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${trackingConfig.googleAdsId}');
      `;
      document.head.appendChild(inlineScript);
    }
  }, [loaded, trackingConfig.googleAdsId, trackingConfig.gtmId]);

  // 追蹤事件方法
  const trackEvent = (eventName, eventParams = {}) => {
    // GTM / GA4 dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventParams
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq('track', eventName, eventParams);
    }
  };

  // 追蹤購買
  const trackPurchase = (orderData) => {
    // GA4 / GTM purchase event
    trackEvent('purchase', {
      transaction_id: orderData.orderNo,
      value: orderData.totalAmount,
      currency: 'TWD',
      items: orderData.items
    });

    // Google Ads 轉換追蹤
    if (window.gtag && trackingConfig.googleAdsConversionId) {
      const [conversionId, conversionLabel] = trackingConfig.googleAdsConversionId.split('/');
      window.gtag('event', 'conversion', {
        'send_to': trackingConfig.googleAdsConversionId,
        'value': orderData.totalAmount,
        'currency': 'TWD',
        'transaction_id': orderData.orderNo
      });
    }

    // Facebook Pixel Purchase
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        value: orderData.totalAmount,
        currency: 'TWD',
        content_ids: orderData.items?.map(i => i.item_id) || [],
        content_type: 'product'
      });
    }
  };

  // 追蹤加入購物車
  const trackAddToCart = (product, quantity = 1) => {
    trackEvent('add_to_cart', {
      items: [{
        item_id: product.id,
        item_name: product.name,
        price: product.sale_price || product.price,
        quantity
      }]
    });
  };

  // 追蹤瀏覽商品
  const trackViewItem = (product) => {
    trackEvent('view_item', {
      items: [{
        item_id: product.id,
        item_name: product.name,
        price: product.sale_price || product.price
      }]
    });
  };

  // 追蹤註冊會員
  const trackSignUp = (method = 'email') => {
    trackEvent('sign_up', { method });
  };

  // 追蹤登入
  const trackLogin = (method = 'password') => {
    trackEvent('login', { method });
  };

  // 追蹤瀏覽商品列表
  const trackViewItemList = (listName, items = []) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: {
        item_list_name: listName,
        items: items.map((p, i) => ({
          item_id: p.id, item_name: p.name,
          price: p.sale_price || p.price, index: i,
        })),
      },
    });
  };

  // 追蹤從列表點擊商品
  const trackSelectItem = (listName, product, index = 0) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_item',
      ecommerce: {
        item_list_name: listName,
        items: [{ item_id: product.id, item_name: product.name, price: product.sale_price || product.price, index }],
      },
    });
  };

  // 追蹤查看購物車
  const trackViewCart = (items = [], value = 0) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_cart',
      ecommerce: { currency: 'TWD', value, items },
    });
  };

  // 追蹤從購物車移除
  const trackRemoveFromCart = (product, quantity = 1) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'remove_from_cart',
      ecommerce: {
        items: [{ item_id: product.id, item_name: product.name, price: product.sale_price || product.price, quantity }],
      },
    });
  };

  // 追蹤進入結帳
  const trackBeginCheckout = (items = [], value = 0) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: { currency: 'TWD', value, items },
    });

    if (window.gtag && trackingConfig.googleAdsConversionId) {
      window.gtag('event', 'conversion', {
        send_to: trackingConfig.googleAdsConversionId.split('/')[0] + '/begin_checkout',
        value, currency: 'TWD',
      });
    }
  };

  // 追蹤填完配送資訊
  const trackAddShippingInfo = (shippingTier, items = [], value = 0) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'add_shipping_info',
      ecommerce: { currency: 'TWD', value, shipping_tier: shippingTier, items },
    });
  };

  // 追蹤填完付款資訊
  const trackAddPaymentInfo = (paymentType, items = [], value = 0) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'add_payment_info',
      ecommerce: { currency: 'TWD', value, payment_type: paymentType, items },
    });
  };

  // 追蹤站內搜尋
  const trackSearch = (searchTerm) => {
    trackEvent('search', { search_term: searchTerm });
  };

  // 追蹤詢價/聯繫
  const trackGenerateLead = (source = '') => {
    trackEvent('generate_lead', { source });
  };

  return (
    <TrackingContext.Provider value={{
      trackingConfig,
      loaded,
      trackEvent,
      trackPurchase,
      trackAddToCart,
      trackViewItem,
      trackSignUp,
      trackLogin,
      trackViewItemList,
      trackSelectItem,
      trackViewCart,
      trackRemoveFromCart,
      trackBeginCheckout,
      trackAddShippingInfo,
      trackAddPaymentInfo,
      trackSearch,
      trackGenerateLead,
    }}>
      {children}
    </TrackingContext.Provider>
  );
};

export default TrackingContext;
